/* Copyright (c) 2020-present CloudIO Inc */

import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './CombinationChartFC';
import { toOptions } from '../../utils/ManagerUtils';

const CombinationChartFCManager: ComponentManagerFn<'CombinationChartFC'> = (
  platform,
  type,
) => {
  return {
    ...baseComponentManager(platform, type),
    type,
    name: 'Fusion Combination Chart',
    isChildAllowed: (page, item, childType) =>
      (['FusionChartsIO'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      caption: {
        label: 'Caption',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Provide a title for the Chart.',
      },
      subCaption: {
        label: 'Sub-Caption',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Provide a sub title for the chart.',
      },
      showDualYAxis: {
        label: 'Show Dual Y Axis',
        type: 'boolean',
        helpText: 'Check this property to show dual y-axis.',
      },
      showDivLines: {
        label: 'Show Div Lines',
        type: 'boolean',
        helpText: 'Enable this to display Horizontal division lines.',
      },
      showXAxisValues: {
        label: 'Show X-Axis Values',
        type: 'boolean',
        helpText: 'Enable this to display values along X-axis.',
      },
      showYAxisValues: {
        label: 'Show Y-Axis Values',
        type: 'boolean',
        helpText: 'Enable this to display values along Y-axis.',
      },
      primaryYAxisMaxValue: {
        label: 'Primary Y Axis Max Value',
        type: 'number',
        helpText:
          'Provide value to explicitly set the upper limit of the Primary Y-axis.',
      },
      primaryYAxisMinValue: {
        label: 'Primary Y Axis Min Value',
        type: 'number',
        helpText:
          'Provide value to explicitly set the lower limit of the Primary Y-axis.',
      },
      showCanvasBase: {
        label: 'Show Canvas Base',
        type: 'boolean',
        helpText: 'Enable this to display the Canvas Base.',
      },
      canvasBaseColor: {
        label: 'Canvas Base Color',
        type: 'string',
        helpText: 'Provide a color in Hex for canvas base. eg.#FF00FF',
      },
      backgroundColor: {
        label: 'BG Color',
        type: 'string',
        helpText: 'Provide hex value to set the background color.',
      },
      secondaryYAxisName: {
        label: 'Secondary Y Axis Name',
        type: 'string',
        helpText:
          'Provide a name that you would want to display on the secondary Y-axis.',
      },
      secondaryYAxisMaxValue: {
        label: 'Secondary Y Axis Max Value',
        type: 'number',
        helpText:
          'Provide value to explicitly set the upper limit of the secondary Y-axis.',
      },
      secondaryYAxisMinValue: {
        label: 'Secondary Y Axis Min Value',
        type: 'number',
        helpText:
          'Provide value to explicitly set the lower limit of the secondary Y-axis.',
      },
      slantLabels: {
        label: 'Slant Labels',
        type: 'boolean',
        helpText: 'Enable this to display labels in slant manner.',
      },
      staggerLines: {
        label: 'Stagger Lines',
        type: 'number',
        helpText:
          'Enter a numeric value above 2 to control how many lines to stagger the label to. By default, all labels are displayed in a single line.',
      },
      labelDisplay: {
        label: 'Label Display',
        type: 'radio',
        options: toOptions(['auto', 'wrap', 'stagger', 'rotate']),
      },
      showLegend: {
        label: 'Show Legend',
        type: 'boolean',
        helpText: 'Enable this to display the legend in the chart.',
      },
      legendPosition: {
        label: 'Legend Position',
        type: 'radio',
        options: toOptions(['Bottom', 'Right']),
      },
      exportEnabled: {
        label: 'Export Enabled',
        type: 'boolean',
        helpText:
          'Enable this to allow export in png, jpg, pdf, svg or xls formats.',
      },
      xAxisName: {
        label: 'X-Axis Name',
        type: 'string',
        helpText:
          'Provide a name that you would want to display on the X-axis.',
      },
      yAxisName: {
        label: 'Y-Axis Name',
        type: 'string',
        helpText:
          'Provide a name that you would want to display on the Y-axis.',
      },
      chartHeight: {
        label: 'Chart Height',
        type: 'number',
        helpText: 'Provide a number to set height of the chart.',
      },
      chartWidth: {
        label: 'Chart Width',
        type: 'number',
        helpText: 'Provide width for the chart.',
      },
      darkBackGround: {
        label: 'Dark Background',
        type: 'boolean',
        helpText:
          'Enable this to display all the labels in lite color when the chart background color is dark.',
      },
      showTrendlines: {
        label: 'Show Trend Lines',
        type: 'boolean',
        category: 'Trend Lines',
        helpText: 'Enable this to show trend lines on the chart.',
      },
      valueOnRight: {
        label: 'Show Value on Right',
        type: 'boolean',
        category: 'Trend Lines',
        helpText: 'Enable this to show label on the right side.',
      },
      dashedLine: {
        label: 'Dashed lines',
        type: 'boolean',
        category: 'Trend Lines',
        helpText: 'Enable this to show the trend lines as dashed line.',
      },
      lineToolText: {
        label: 'Line ToolText',
        type: 'string',
        category: 'Trend Lines',
        helpText:
          'You can specify the ToolText string to be displayed when hovering over the trend line.',
      },
      lineColor: {
        label: 'Line Color',
        type: 'string',
        category: 'Trend Lines',
        helpText:
          'Color of the line in hex e.g., "#FF0000, #0372AB, #FF5904...".',
      },
      displayValue: {
        label: 'Label text',
        type: 'string',
        category: 'Trend Lines',
        helpText:
          'Label of the trend line. If the label field is empty, it will take the value of the trend line.',
      },
      startValue: {
        label: 'Start Value',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Trend Lines',
        helpText: 'Provide a start value for the Trend line.',
      },
      endValue: {
        label: 'End Value',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Trend Lines',
        helpText:
          'Provide an end value for the trend line. If not specified, the trend line will extend as a straight line from the start value.',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'secondaryYAxisName':
        case 'secondaryYAxisMaxValue':
        case 'secondaryYAxisMinValue':
          return itemData.showDualYAxis;
        case 'legendPosition':
          return itemData.showLegend;
        case 'staggerLines':
          return itemData.labelDisplay === 'stagger';
        case 'slantLabels':
          return itemData.labelDisplay === 'rotate';
        case 'canvasBaseColor':
          return itemData.showCanvasBase;
        case 'dashedLine':
        case 'startValue':
        case 'endValue':
        case 'lineColor':
        case 'valueOnRight':
        case 'displayValue':
        case 'lineToolText':
          return itemData.showTrendlines;
        default:
          return true;
      }
    },
    category: 'Dashboard',
    icon: { iconName: 'chart-mixed' },
    description: `**Overview**<br/>
The Fusion Combination Chart is a advanced data visualization tool that allows you to overlay multiple chart types—such as Columns, Lines, and Areas—within a single view. This component is essential for comparing metrics that share a common timeline or category axis but may differ in scale or unit.
By combining different visualizations, you can reveal correlations that would be missed if the data were split across separate charts. For example, plotting "Total Sales" (Bar) against "Growth Rate" (Line) provides an immediate sense of performance momentum.

**1. Titles & Axes Configuration**<br/>
Define the context of your data with clear labeling and axis management.
* **Caption & Sub-Caption:** Sets the primary and secondary titles of the chart. These fields support expressions, allowing dynamic updates based on user filters (e.g., "Sales Performance: 2024").
* **X-Axis / Y-Axis Name:** Labels the horizontal and vertical axes to indicate units (e.g., "Months" vs "Revenue ($)").
* **Show Dual Y Axis:** Enables a secondary vertical axis on the right side of the chart. (Use Case: Essential when plotting two datasets with vastly different scales, such as "Revenue" in Millions vs "Profit Margin" in Percentage).
* **Secondary Axis Name:** Labels the right-side axis.
* **Min/Max Values:** Explicitly sets the scale limits for both Primary and Secondary axes to prevent skewing or to focus on a specific range.

**2. Data & Series Integration**<br/>
The chart acts as a visual wrapper. To render data, it relies on a specific child component.
* **Required Child:** You must add a **FusionChartsIO** component inside this chart. The FusionChartsIO component handles the actual data fetching, series definition, and mapping of fields to chart types (e.g., "Series A = Column", "Series B = Line"). Without this child, the chart will remain empty.

**3. Visual Customization**<br/>
Tailor the chart's aesthetic to match your dashboard's theme.
* **Background Color:** Sets the fill color of the entire chart area.
* **Canvas Base:** Toggles a visual base (floor) for 3D-like effects. You can customize the Canvas Base Color if enabled.
* **Dark Background:** A toggle that automatically adjusts label and text colors to be legible against dark backgrounds.
* **Grid & Div Lines:** "Show Div Lines" displays horizontal grid lines to help users estimate values.
* **Label Display:** Controls how X-axis labels behave when space is limited. (Options: Auto, Wrap, Stagger, or Rotate).
* **Slant Labels:** Tilts labels for better fit when using rotation.

**4. Trend Lines**<br/>
Add context to your data by drawing static lines that indicate targets, limits, or averages.
* **Show Trend Lines:** Enables the trend line feature.
* **Start / End Value:** Defines where the line begins and ends on the Y-axis. (e.g., Start=50, End=50 draws a flat target line at 50).
* **Line Styling:** Customize the Line Color, enable Dashed Lines, or add Tooltips (Line ToolText) that appear when hovering over the line.
* **Label Text:** Adds a text label (e.g., "Annual Target") to the line. You can choose to position this Value on Right.

**5. Export Capabilities**<br/>
* **Export Enabled:** Adds a menu to the chart allowing users to download the visualization as a PNG, JPG, PDF, SVG, or Excel file. This is crucial for reporting workflows.`,
  };
};

export default CombinationChartFCManager;
